import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// iter-63: per-section vault — map every protected backend URL to its section
// so the interceptor can attach the correct `X-Vault-Token` + `X-Vault-Section`
// header. Each section's session lives under `cc_vault_token__<section>`.
// iter-81ac: staff_docs merged into licensing (one shared passcode for the
// entire sensitive cluster — licensing file, staffs file, drill log,
// playground, food menu, staff app, substitutes).
// iter-81an: switched from a hand-maintained prefix list to a *regex*
// matcher so newly added vault-gated endpoints (e.g. /staff/compliance/*,
// /staff/documents/ai-rescan) can never silently miss the vault header.
// If it's under /staff/, /playground*, /drill-logs*, /menus*, /center/licensing*,
// it's licensing. If it's /attendance/daily-pdf, /compliance/cases*, or
// /audit/binder*, it's records. Everything else is unguarded.
const LICENSING_RE = /^(?:\/api)?\/(staff|playground|playground-inspections|drill-logs|menus|center\/licensing)(\/|$|\?)/;
const RECORDS_RE = /^(?:\/api)?\/(attendance\/daily-pdf|compliance\/cases|audit\/binder)(\/|$|\?)/;

function _matchVaultSection(url) {
  if (!url) return null;
  if (LICENSING_RE.test(url)) return "licensing";
  if (RECORDS_RE.test(url)) return "records";
  return null;
}

const client = axios.create({ baseURL: API });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem("cc_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const section = _matchVaultSection(config.url);
  if (section) {
    const vt = localStorage.getItem(`cc_vault_token__${section}`);
    if (vt) {
      config.headers["X-Vault-Token"] = vt;
      config.headers["X-Vault-Section"] = section;
    }
    // Stash the section on the config so the response interceptor can tell
    // which section's token to clear if we get a 401 back.
    config._vaultSection = section;
  }
  // Auto-attach billing token (existing behavior, kept for /billing/* endpoints).
  if (config.url && config.url.includes("/billing/") && !config.url.includes("/passcode/")) {
    const bt = sessionStorage.getItem("cc_billing_token");
    if (bt) config.headers["X-Billing-Token"] = bt;
  }
  return config;
});

client.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err.response?.status;
    const detail = err.response?.data?.detail || "";
    const isVaultError =
      status === 423 ||
      (status === 401 && typeof detail === "string" && /vault/i.test(detail));
    const isBillingError =
      status === 401 && typeof detail === "string" && /billing/i.test(detail);

    // Center suspended / canceled / deactivated by superadmin. Force-logout
    // everyone in this center and drop them at a dedicated screen so they
    // can't just keep clicking around with a cached JWT.
    const isCenterSuspended =
      status === 403 && typeof detail === "object" && detail?.code === "CENTER_SUSPENDED";
    if (isCenterSuspended) {
      const params = new URLSearchParams({
        status: detail.status || "suspended",
        center: detail.center_name || "",
      }).toString();
      localStorage.removeItem("cc_token");
      localStorage.removeItem("cc_user");
      // iter-81ao: vault tokens now live in localStorage (persist until logout)
      Object.keys(localStorage).filter((k) => k.startsWith("cc_vault_token")).forEach((k) => localStorage.removeItem(k));
      Object.keys(sessionStorage).filter((k) => k.startsWith("cc_vault_token")).forEach((k) => sessionStorage.removeItem(k));
      sessionStorage.removeItem("cc_billing_token");
      if (!window.location.pathname.startsWith("/suspended")) {
        window.location.href = `/suspended?${params}`;
      }
      return Promise.reject(err);
    }

    // iter-81an: on a real vault 401, immediately clear the stale token for
    // that section and dispatch a custom event so the VaultLock component
    // re-renders its "locked" screen instantly.
    if (isVaultError && status === 401) {
      const sec = err.config?._vaultSection;
      if (sec) {
        localStorage.removeItem(`cc_vault_token__${sec}`);
        sessionStorage.removeItem(`cc_vault_token__${sec}`);  // legacy cleanup
        try {
          window.dispatchEvent(new CustomEvent("cc-vault-relock", { detail: { section: sec } }));
        } catch (_) { /* noop */ }
      }
    }

    // Vault/billing 401s must NOT log the user out — those are passcode prompts,
    // not session-expired errors.
    if (status === 401 && !isVaultError && !isBillingError) {
      localStorage.removeItem("cc_token");
      localStorage.removeItem("cc_user");
      // Clear every per-section vault session token
      Object.keys(localStorage).filter((k) => k.startsWith("cc_vault_token")).forEach((k) => localStorage.removeItem(k));
      Object.keys(sessionStorage).filter((k) => k.startsWith("cc_vault_token")).forEach((k) => sessionStorage.removeItem(k));
      sessionStorage.removeItem("cc_billing_token");
      if (!window.location.pathname.startsWith("/login") && !window.location.pathname.startsWith("/register")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export default client;
export { API };

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" · ");
  // Structured errors from the API — e.g. { code:"CENTER_SUSPENDED", message:"…" }
  if (typeof detail === "object" && detail.message) return detail.message;
  if (typeof detail === "object" && detail.msg) return detail.msg;
  return String(detail);
}

// iter-81an: helper used by callers to know if an error is a vault re-lock
// so they can suppress the (redundant) toast — the VaultLock overlay will
// already be rendering the lock screen.
export function isVaultRelockError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail || "";
  return status === 401 && typeof detail === "string" && /vault/i.test(detail);
}
